import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Icon } from "@majourneys/icons";
import {
  CREATOR_FOLLOWER_RANGES,
  CREATOR_PLATFORMS,
  SUPPORTED_LANGS,
  type AmbassadorApplicationInput,
  type CreatorFollowerRange,
  type CreatorPlatform,
} from "@majourneys/shared";
import { FOLLOWER_LABEL_KEYS, PLATFORM_ICONS, PLATFORM_LABEL_KEYS } from "../../creators/constants/creator-options";
import {
  Chip,
  ERROR_TEXT,
  FIELD,
  FIELD_ERROR,
  FIELD_REST,
  FieldShell,
  GREEN,
  MUTED,
  SEND_BEAT_MS,
  primaryClass,
  primaryStyle,
  prefersReducedMotion,
  shake,
} from "./kit";

/** sessionStorage, so the answers survive the sign-in round trip and a refresh. */
export const APPLY_DRAFT_KEY = "mj_ambassador_draft";

const ABOUT_MAX = 2000;

type T = (key: string, opts?: Record<string, unknown>) => string;

const LINK_RE = /^https?:\/\/[^\s/]+\.[^\s]+$/i;
const isLink = (v: string | undefined) => LINK_RE.test((v ?? "").trim());

/** Literal keys, so the locale-key checks can see every one. */
const LINK_LABEL_KEYS: Record<CreatorPlatform, string> = {
  instagram: "ambassadors.form.link_instagram",
  tiktok: "ambassadors.form.link_tiktok",
  youtube: "ambassadors.form.link_youtube",
  other: "ambassadors.form.link_other",
};
const LINK_PLACEHOLDER_KEYS: Record<CreatorPlatform, string> = {
  instagram: "ambassadors.form.link_instagram_placeholder",
  tiktok: "ambassadors.form.link_tiktok_placeholder",
  youtube: "ambassadors.form.link_youtube_placeholder",
  other: "ambassadors.form.link_other_placeholder",
};

/** Same rules as the API's `ambassadorApplicationInputSchema`, with our own words. */
export function buildApplySchema(t: T) {
  return z.object({
    fullName: z.string().trim().min(2, t("ambassadors.form.err_name")).max(120),
    email: z.string().trim().email(t("ambassadors.form.err_email")).max(200),
    platforms: z
      .array(
        z.object({
          platform: z.enum(CREATOR_PLATFORMS),
          url: z.string().trim().max(500).refine(isLink, t("ambassadors.form.err_profile_url")),
        }),
      )
      .min(1, t("ambassadors.form.err_platforms")),
    followers: z.enum(CREATOR_FOLLOWER_RANGES, { error: () => t("ambassadors.form.err_followers") }),
    about: z.string().trim().min(10, t("ambassadors.form.err_about")).max(ABOUT_MAX),
  });
}

/** A field with its own error: `platforms` (the chips) or `link-<platform>`. */
type Field = "fullName" | "email" | "platforms" | `link-${CreatorPlatform}` | "followers" | "about";

export type ApplyDraft = {
  fullName: string;
  email: string;
  /** Picked platforms, in the order they were picked. */
  platforms: CreatorPlatform[];
  /**
   * Every link typed so far, picked or not: un-picking a platform hides its
   * field, picking it again brings the link back.
   */
  links: Partial<Record<CreatorPlatform, string>>;
  followers: CreatorFollowerRange | null;
  about: string;
};

const EMPTY: ApplyDraft = {
  fullName: "",
  email: "",
  platforms: [],
  links: {},
  followers: null,
  about: "",
};

const isPlatform = (v: unknown): v is CreatorPlatform =>
  typeof v === "string" && (CREATOR_PLATFORMS as readonly string[]).includes(v);

/**
 * A stored draft, made safe. Drafts saved before 2026-10-09 hold one
 * `platform` + `profileUrl`; they become a one-entry list.
 */
export function migrateDraft(raw: unknown): ApplyDraft {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return EMPTY;
  const r = raw as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  const links: ApplyDraft["links"] = {};
  if (r.links && typeof r.links === "object") {
    for (const [k, v] of Object.entries(r.links as Record<string, unknown>)) {
      if (isPlatform(k) && typeof v === "string") links[k] = v;
    }
  }
  let platforms: CreatorPlatform[] = Array.isArray(r.platforms)
    ? [...new Set(r.platforms.filter(isPlatform))]
    : [];
  if (!Array.isArray(r.platforms) && isPlatform(r.platform)) {
    platforms = [r.platform];
    if (str(r.profileUrl) && !links[r.platform]) links[r.platform] = str(r.profileUrl);
  }
  return {
    fullName: str(r.fullName),
    email: str(r.email),
    platforms,
    links,
    followers: (CREATOR_FOLLOWER_RANGES as readonly string[]).includes(str(r.followers))
      ? (r.followers as CreatorFollowerRange)
      : null,
    about: str(r.about),
  };
}

function readDraft(): ApplyDraft {
  if (typeof window === "undefined") return EMPTY;
  try {
    return migrateDraft(JSON.parse(window.sessionStorage.getItem(APPLY_DRAFT_KEY) ?? "{}"));
  } catch {
    return EMPTY;
  }
}

/** A profile link built from the hero's platform and handle, when we can. */
export function profileUrlFor(platform: CreatorPlatform, handle: string | null): string {
  if (!handle) return "";
  if (platform === "instagram") return `https://instagram.com/${handle}`;
  if (platform === "tiktok") return `https://tiktok.com/@${handle}`;
  if (platform === "youtube") return `https://youtube.com/@${handle}`;
  return "";
}

export interface ApplyCarry {
  platform: CreatorPlatform;
  profileUrl: string;
  /** Changes on every Apply press, so pressing twice still carries. */
  nonce: number;
}

export function ApplyForm(props: {
  signedIn: boolean;
  defaultEmail?: string;
  defaultName?: string;
  carry?: ApplyCarry | null;
  onSubmit: (input: AmbassadorApplicationInput) => Promise<void>;
  /** Signed-out visitors: the draft is already saved, open sign-in. */
  onNeedSignIn: () => void;
  /** After the send beat. */
  onSent?: () => void;
  /** The picked platforms, for the live ambassador card. */
  onPlatformsChange?: (platforms: CreatorPlatform[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const [draft, setDraft] = useState<ApplyDraft>(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Read the draft after mount: sessionStorage does not exist on the server.
  useEffect(() => {
    setDraft(readDraft());
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    setDraft((d) => ({
      ...d,
      email: d.email || props.defaultEmail || "",
      fullName: d.fullName || props.defaultName || "",
    }));
  }, [hydrated, props.defaultEmail, props.defaultName]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      window.sessionStorage.setItem(APPLY_DRAFT_KEY, JSON.stringify(draft));
    } catch {
      // Private mode: the form still works, the draft just does not survive.
    }
  }, [draft, hydrated]);

  const onPlatformsChange = props.onPlatformsChange;
  const platformsKey = draft.platforms.join(",");
  useEffect(() => {
    if (hydrated) onPlatformsChange?.(draft.platforms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, platformsKey, onPlatformsChange]);

  // The hero's Apply hands its platform and handle down: that platform is
  // picked (next to any already picked) and its link filled when we built one.
  // Waits for the draft, so a carry on first paint is not overwritten by it.
  useEffect(() => {
    if (!props.carry || !hydrated) return;
    const { platform, profileUrl } = props.carry;
    setDraft((d) => ({
      ...d,
      platforms: d.platforms.includes(platform) ? d.platforms : [...d.platforms, platform],
      links: profileUrl ? { ...d.links, [platform]: profileUrl } : d.links,
    }));
    setErrors((e) => ({ ...e, platforms: undefined }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.carry?.nonce, hydrated]);

  function set<K extends "fullName" | "email" | "followers" | "about">(key: K, value: ApplyDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function togglePlatform(p: CreatorPlatform) {
    setDraft((d) => ({
      ...d,
      platforms: d.platforms.includes(p) ? d.platforms.filter((x) => x !== p) : [...d.platforms, p],
    }));
    setErrors((e) => ({ ...e, platforms: undefined, [`link-${p}`]: undefined }));
  }

  function setLink(p: CreatorPlatform, value: string) {
    setDraft((d) => ({ ...d, links: { ...d.links, [p]: value } }));
    if (errors[`link-${p}`]) setErrors((e) => ({ ...e, [`link-${p}`]: undefined }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (sending || sent) return;
    setFailed(false);
    const parsed = buildApplySchema(t).safeParse({
      ...draft,
      platforms: draft.platforms.map((platform) => ({ platform, url: draft.links[platform] ?? "" })),
    });
    if (!parsed.success) {
      const next: Partial<Record<Field, string>> = {};
      for (const issue of parsed.error.issues) {
        const [head, index] = issue.path;
        const key: Field =
          head === "platforms" && typeof index === "number"
            ? `link-${draft.platforms[index]!}`
            : (head as Field);
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      shake(formRef.current);
      const order: Field[] = [
        "fullName",
        "email",
        "platforms",
        ...draft.platforms.map((p): Field => `link-${p}`),
        "followers",
        "about",
      ];
      const first = order.find((k) => next[k]);
      if (first) window.setTimeout(() => document.getElementById(`apply-${first}`)?.focus(), 0);
      return;
    }
    setErrors({});
    if (!props.signedIn) {
      props.onNeedSignIn();
      return;
    }
    setSending(true);
    try {
      const lang = (SUPPORTED_LANGS as readonly string[]).includes(i18n.language)
        ? (i18n.language as AmbassadorApplicationInput["lang"])
        : undefined;
      await props.onSubmit({ ...parsed.data, lang });
      try {
        window.sessionStorage.removeItem(APPLY_DRAFT_KEY);
      } catch {
        /* nothing to clear */
      }
      setSent(true);
      window.setTimeout(() => props.onSent?.(), prefersReducedMotion() ? 0 : SEND_BEAT_MS);
    } catch {
      setFailed(true);
      shake(formRef.current);
    } finally {
      setSending(false);
    }
  }

  const forward = isRtl ? "ri-arrow-left-line" : "ri-arrow-right-line";

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="grid gap-5" data-testid="ambassador-apply-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <FieldShell id="apply-fullName" label={t("ambassadors.form.full_name")} error={errors.fullName}>
          <input
            id="apply-fullName"
            autoComplete="name"
            value={draft.fullName}
            onChange={(e) => set("fullName", e.target.value)}
            aria-invalid={errors.fullName ? true : undefined}
            aria-describedby={errors.fullName ? "apply-fullName-error" : undefined}
            className={`${FIELD} h-[46px] px-4 ${errors.fullName ? FIELD_ERROR : FIELD_REST}`}
          />
        </FieldShell>
        <FieldShell id="apply-email" label={t("ambassadors.form.email")} error={errors.email}>
          <input
            id="apply-email"
            type="email"
            dir="ltr"
            autoComplete="email"
            value={draft.email}
            onChange={(e) => set("email", e.target.value)}
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={errors.email ? "apply-email-error" : undefined}
            className={`${FIELD} h-[46px] px-4 ${errors.email ? FIELD_ERROR : FIELD_REST}`}
          />
        </FieldShell>
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        <span id="apply-platforms-label" className="font-sans text-[12.5px] font-semibold text-[#110E39]">
          {t("ambassadors.form.platforms")}
        </span>
        <p id="apply-platforms-hint" className="font-sans text-[12px]" style={{ color: MUTED }}>
          {t("ambassadors.form.platforms_hint")}
        </p>
        <div
          role="group"
          aria-labelledby="apply-platforms-label"
          aria-describedby={errors.platforms ? "apply-platforms-hint apply-platforms-error" : "apply-platforms-hint"}
          className="flex flex-wrap gap-2"
        >
          {CREATOR_PLATFORMS.map((p, i) => {
            const on = draft.platforms.includes(p);
            return (
              <Chip
                key={p}
                id={i === 0 ? "apply-platforms" : undefined}
                on={on}
                error={Boolean(errors.platforms)}
                onClick={() => togglePlatform(p)}
              >
                {on ? null : <Icon name={PLATFORM_ICONS[p]} className="text-[15px]" aria-hidden />}
                {t(PLATFORM_LABEL_KEYS[p])}
              </Chip>
            );
          })}
        </div>
        {errors.platforms ? (
          <p id="apply-platforms-error" className={`mj-fade-in ${ERROR_TEXT}`}>
            {errors.platforms}
          </p>
        ) : null}
      </div>

      {draft.platforms.length ? (
        <div className="grid gap-4">
          {draft.platforms.map((p) => {
            const id = `apply-link-${p}`;
            const error = errors[`link-${p}`];
            const value = draft.links[p] ?? "";
            const ok = !error && isLink(value);
            return (
              <FieldShell key={p} id={id} label={t(LINK_LABEL_KEYS[p])} error={error}>
                <div className="relative mj-fade-in">
                  <input
                    id={id}
                    type="url"
                    dir="ltr"
                    autoComplete="url"
                    inputMode="url"
                    value={value}
                    onChange={(e) => setLink(p, e.target.value)}
                    placeholder={t(LINK_PLACEHOLDER_KEYS[p])}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? `${id}-error` : undefined}
                    className={`${FIELD} h-[46px] pe-11 ps-4 ${error ? FIELD_ERROR : FIELD_REST}`}
                  />
                  {ok ? (
                    <Icon
                      name="ri-checkbox-circle-line"
                      data-testid={`${id}-ok`}
                      className="mj-pop-in pointer-events-none absolute end-3.5 top-1/2 -translate-y-1/2 text-[18px]"
                      color={GREEN}
                      aria-hidden
                    />
                  ) : null}
                </div>
              </FieldShell>
            );
          })}
        </div>
      ) : null}

      <div className="flex min-w-0 flex-col gap-1.5">
        <span id="apply-followers-label" className="font-sans text-[12.5px] font-semibold text-[#110E39]">
          {t("ambassadors.form.followers")}
        </span>
        <p id="apply-followers-hint" className="font-sans text-[12px]" style={{ color: "#5a5d77" }}>
          {t("ambassadors.form.followers_hint")}
        </p>
        <div
          role="group"
          aria-labelledby="apply-followers-label"
          aria-describedby={errors.followers ? "apply-followers-hint apply-followers-error" : "apply-followers-hint"}
          className="flex flex-wrap gap-2"
        >
          {CREATOR_FOLLOWER_RANGES.map((r, i) => (
            <Chip
              key={r}
              id={i === 0 ? "apply-followers" : undefined}
              on={draft.followers === r}
              error={Boolean(errors.followers)}
              onClick={() => set("followers", r)}
            >
              {t(FOLLOWER_LABEL_KEYS[r])}
            </Chip>
          ))}
        </div>
        {errors.followers ? (
          <p id="apply-followers-error" className={`mj-fade-in ${ERROR_TEXT}`}>
            {errors.followers}
          </p>
        ) : null}
      </div>

      <FieldShell
        id="apply-about"
        label={t("ambassadors.form.about")}
        error={errors.about}
        hint={
          <p className="font-sans text-[12px]" style={{ color: "#5a5d77" }}>
            {t("ambassadors.form.about_hint")}
          </p>
        }
      >
        <textarea
          id="apply-about"
          rows={4}
          maxLength={ABOUT_MAX}
          value={draft.about}
          onChange={(e) => set("about", e.target.value)}
          placeholder={t("ambassadors.form.about_placeholder")}
          aria-invalid={errors.about ? true : undefined}
          aria-describedby={errors.about ? "apply-about-error" : undefined}
          className={`${FIELD} min-h-[112px] resize-none px-4 py-3 leading-relaxed ${
            errors.about ? FIELD_ERROR : FIELD_REST
          }`}
        />
      </FieldShell>

      {failed ? (
        <p className={`mj-fade-in ${ERROR_TEXT}`} role="alert">
          {t("ambassadors.form.error_generic")}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={sending || sent}
        aria-live="polite"
        className={primaryClass("lg", true)}
        style={primaryStyle}
      >
        {sent ? (
          <>
            <Icon name="ri-checkbox-circle-line" className="mj-pop-in text-[18px]" aria-hidden />
            {t("ambassadors.form.sent")}
          </>
        ) : sending ? (
          <>
            <Icon name="ri-loader-4-line" className="animate-spin text-[18px]" aria-hidden />
            {t("ambassadors.form.sending")}
          </>
        ) : (
          <>
            {props.signedIn ? t("ambassadors.form.submit") : t("ambassadors.form.submit_sign_in")}
            <Icon name={forward} aria-hidden />
          </>
        )}
      </button>
    </form>
  );
}
